import { batch } from 'react-redux';

import { handleErrors } from './ErrorActions';
import { getNewNotifications } from './NotificationsActions';
import { forceSavedSearchRefresh } from './SavedSearchesActions';
import { insertSubmissionCodesImport } from '../api/master-setup-import';
import {
  BULK_IMPORT_APIS,
  IMPORT_POLLING_TIME,
  IMPORT_STEPS,
  STATUSES,
} from '../constants/MasterSetupConstants';
import { preprocessImportStatuses } from '../utils/commonUtils';
import { downloadFile } from '../utils/domUtils';
import { getErrorMessage, getImportApiCall } from '../utils/importUtils';

export const INITIATE_IMPORT_PROCESS = 'INITIATE_IMPORT_PROCESS';

export const SET_IMPORT_STEP = 'SET_IMPORT_STEP';
export const SET_MASTER_SETUP_IMPORT_DATA = 'SET_MASTER_SETUP_IMPORT_DATA';
export const SET_MASTER_SETUP_IMPORT_LOADING = 'SET_MASTER_SETUP_IMPORT_LOADING';
export const SET_IMPORT_MODAL_OPEN = 'SET_IMPORT_MODAL_OPEN';
export const SET_IMPORT_ERROR = 'SET_IMPORT_ERROR';
export const CLEAR_IMPORT_ENTITY_STORE = 'CLEAR_IMPORT_ENTITY_STORE';
export const SET_MODAL_IN_FILE_PROCESSING_STATE = 'SET_MODAL_IN_FILE_PROCESSING_STATE';

export const initiateImportProcess = (entityName) => ({
  type: INITIATE_IMPORT_PROCESS,
  payload: { entityName },
});

export const setImportModalOpen = (flag) => ({
  type: SET_IMPORT_MODAL_OPEN,
  payload: { flag },
});

export const setImportStep = (step, entityName) => ({
  type: SET_IMPORT_STEP,
  payload: { step, entityName },
});

export const clearImportEntityStore = (entityName) => ({
  type: CLEAR_IMPORT_ENTITY_STORE,
  payload: { entityName },
});

const setImportLoading = (flag, entityName) => ({
  type: SET_MASTER_SETUP_IMPORT_LOADING,
  payload: { flag, entityName },
});

const setMasterSetupImportData = (importKey, importStatuses, entityName) => ({
  type: SET_MASTER_SETUP_IMPORT_DATA,
  payload: { importKey, importStatuses, entityName },
});

const setImportError = (flag, message, entityName) => ({
  type: SET_IMPORT_ERROR,
  payload: { flag, message, entityName },
});

const setModalInFileProcessingState = (entityName, importKey) => ({
  type: SET_MODAL_IN_FILE_PROCESSING_STATE,
  payload: { entityName, importKey },
});

export const checkStatus = ({
  importKey,
  status,
  isWorkflowMasterSetup,
  dispatch,
  entityName,
  link,
  importStatuses,
}) => {
  switch (status) {
    case STATUSES.IN_PROGRESS: {
      setTimeout(async () => {
        getMasterSetupImportStatus({
          importKey,
          isWorkflowMasterSetup,
          dispatch,
          entityName,
          link,
        });
      }, IMPORT_POLLING_TIME);
      return;
    }

    case STATUSES.COMPLETED: {
      const hasCompletedRecord = importStatuses.find(
        (record) => record.StatusCode === STATUSES.COMPLETED
      );

      batch(() => {
        dispatch(setImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, entityName));
        dispatch(setImportLoading(false, entityName));
        if (hasCompletedRecord) {
          dispatch(forceSavedSearchRefresh(entityName, true));
        }
      });
      return;
    }

    case STATUSES.ERROR: {
      batch(() => {
        dispatch(
          setImportError(true, 'Error encountered during import. Please try again', entityName)
        );
        dispatch(setImportLoading(false, entityName));
      });
      return;
    }

    case STATUSES.NOT_DONE: {
      batch(() => {
        dispatch(
          setImportError(true, 'Imported file is invalid or empty. Please try again', entityName)
        );
        dispatch(setImportLoading(false, entityName));
      });
      return;
    }

    default: {
      batch(() => {
        dispatch(setImportError(true, 'Unexpected Status', entityName));
        dispatch(setImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, entityName));
        dispatch(setImportLoading(false, entityName));
      });
      return;
    }
  }
};

const handleImportApiCallFail = ({ errors, dispatch, entityName }) => {
  const errorMessage = getErrorMessage(errors, 'Error encountered during import. Please try again');

  batch(() => {
    dispatch(setImportError(true, errorMessage, entityName));
    dispatch(setImportLoading(false, entityName));
  });
};

export const getMasterSetupImportStatus = async ({
  importKey,
  isWorkflowMasterSetup,
  dispatch,
  entityName,
  link,
}) => {
  const checkStatusApi = getImportApiCall(isWorkflowMasterSetup, BULK_IMPORT_APIS.CHECK_STATUS);
  const masterSetupImportStatusResponse = await checkStatusApi({ importKey });

  if (!masterSetupImportStatusResponse.hasError) {
    const {
      ImportStatuses: importStatuses,
      OverallStatusCode: status,
    } = masterSetupImportStatusResponse;
    const preprocessedImportStatuses = preprocessImportStatuses(importStatuses);

    dispatch(setMasterSetupImportData(importKey, preprocessedImportStatuses, entityName));
    checkStatus({
      importKey,
      status,
      isWorkflowMasterSetup,
      dispatch,
      entityName,
      link,
      importStatuses,
    });
  } else {
    handleImportApiCallFail({
      errors: masterSetupImportStatusResponse.errors,
      dispatch,
      entityName,
      link,
      importKey,
    });
  }
};

export const importMasterSetup = ({ importKey, isWorkflowMasterSetup, entityName, link }) => async (
  dispatch
) => {
  dispatch(setImportStep(IMPORT_STEPS.FILE_PROCESSING, entityName));

  const importApi = getImportApiCall(isWorkflowMasterSetup, BULK_IMPORT_APIS.IMPORT);
  const importMasterSetupResponse = await importApi({ importKey });

  if (!importMasterSetupResponse.hasError) {
    getMasterSetupImportStatus({
      importKey,
      isWorkflowMasterSetup,
      dispatch,
      entityName,
      link,
    });
  } else {
    handleImportApiCallFail({
      errors: importMasterSetupResponse.errors,
      dispatch,
      entityName,
      link,
      importKey,
    });
  }
};

export const initiateMasterSetupImport = (
  file,
  isWorkflowMasterSetup,
  { entityName, link, ...restImportProps }
) => async (dispatch) => {
  batch(() => {
    dispatch(setImportLoading(true, entityName));
    dispatch(setImportStep(IMPORT_STEPS.START_IMPORT, entityName));
  });

  const initiateImportApi = getImportApiCall(
    isWorkflowMasterSetup,
    BULK_IMPORT_APIS.INITIATE_IMPORT
  );

  const initiateMasterSetupImportResponse = await initiateImportApi(file, link, {
    ...restImportProps,
    entityName,
  });

  if (!initiateMasterSetupImportResponse.hasError) {
    const {
      ImportKey: importKey,
      ImportStatuses: importStatuses,
    } = initiateMasterSetupImportResponse;

    const requestNotificationDelay = process.env.NODE_ENV === 'test' ? 0 : 10000;

    const preprocessedImportStatuses = preprocessImportStatuses(importStatuses);

    batch(() => {
      dispatch(setMasterSetupImportData(importKey, preprocessedImportStatuses, entityName));
      dispatch(
        importMasterSetup({
          importKey,
          isWorkflowMasterSetup,
          entityName,
          link,
        })
      );
      //force notification refresh after 10 second when import is initiated
      setTimeout(() => {
        dispatch(getNewNotifications());
      }, requestNotificationDelay);
    });
  } else {
    handleImportApiCallFail({
      errors: initiateMasterSetupImportResponse.errors,
      dispatch,
      entityName,
      link,
    });
  }
};

export const initiateSubmissionCodeImport = (
  file,
  { entityName, link, ...restImportProps }
) => async (dispatch) => {
  batch(() => {
    dispatch(setImportLoading(true, entityName));
    dispatch(setImportStep(IMPORT_STEPS.FILE_PROCESSING, entityName));
  });

  const insertSubmissionCodesImportResponse = await insertSubmissionCodesImport(file, link, {
    ...restImportProps,
    entityName,
  });

  if (!insertSubmissionCodesImportResponse.hasError) {
    const requestNotificationDelay = process.env.NODE_ENV === 'test' ? 0 : 10000;

    batch(() => {
      //force notification refresh after 10 second when import is initiated
      setTimeout(() => {
        dispatch(getNewNotifications());
      }, requestNotificationDelay);
      dispatch(setImportLoading(false, entityName));
      dispatch(setImportStep(IMPORT_STEPS.UPLOAD_COMPLETED, entityName));
    });
  } else {
    handleImportApiCallFail({
      errors: insertSubmissionCodesImportResponse.errors,
      dispatch,
      entityName,
      link,
    });
  }
};

export const handleImportModalOpen = (entityName, importKey, isWorkflowMasterSetup) => (
  dispatch,
  getState
) => {
  if (!entityName || !importKey) {
    dispatch(setImportModalOpen(true));
    return;
  }
  const { importEntities } = getState().bulkImport;
  const isAlreadyInStore = Object.keys(importEntities).includes(entityName);
  const shouldGetCurrentImportResults =
    !isAlreadyInStore ||
    (isAlreadyInStore &&
      (importEntities[entityName].importStep < IMPORT_STEPS.START_IMPORT ||
        importEntities[entityName].importKey !== importKey));

  if (shouldGetCurrentImportResults) {
    dispatch(setModalInFileProcessingState(entityName, importKey));
    getMasterSetupImportStatus({
      importKey,
      isWorkflowMasterSetup,
      dispatch,
      entityName,
    });
  }
  dispatch(setImportModalOpen(true));
};

export const getBulkImportHistory = ({ importEntityKey, isWorkflowMasterSetup }) => async (
  dispatch
) => {
  const importHistoryApi = getImportApiCall(isWorkflowMasterSetup, BULK_IMPORT_APIS.IMPORT_HISTORY);

  const importHistoryResponse = await importHistoryApi({ importEntityKey });

  if (importHistoryResponse.hasError) {
    dispatch(handleErrors(importHistoryResponse.errors));
    return [];
  }
  return isWorkflowMasterSetup
    ? importHistoryResponse.EntityImportHistory?.map((item) => ({ ...item, isWorkflowMasterSetup }))
    : importHistoryResponse.MasterSetupImportHistory;
};

export const downloadBulkImportFile = ({ downloadType, isWorkflowMasterSetup, apiProps }) => async (
  dispatch
) => {
  const downloadFileApi = getImportApiCall(isWorkflowMasterSetup, downloadType);
  const { data, headers } = await downloadFileApi(apiProps);

  const contentType = headers['content-type'];

  if (data.hasError) {
    dispatch(handleErrors(data.errors));
    return;
  }

  // if content-type is json, it's an error response
  if (contentType === 'application/json') {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      const { errors = [] } = JSON.parse(reader.result);
      dispatch(handleErrors(errors));
    });

    reader.readAsText(data);

    return;
  }

  let filename = '';
  const contentDisposition = headers['content-disposition'];

  if (contentDisposition) {
    filename = contentDisposition.split('filename=')[1];
  }

  downloadFile(filename, data, 0);
};
